import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import LogoAtivaJa from "@/components/LogoAtivaJa";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, MapPin, Users, AlertTriangle, XCircle, ShieldAlert, ArrowRight, ArrowLeft, CreditCard, Star } from "lucide-react";

export default function ClienteParticular() {
  const [etapa, setEtapa] = useState("local"); // local | resultado | termo
  const [cep, setCep] = useState("");
  const [manual, setManual] = useState(false);
  const [loc, setLoc] = useState({ estado: "", cidade: "", bairro: "" });
  const [resultado, setResultado] = useState(null);
  const [aceite, setAceite] = useState(false);
  const [loading, setLoading] = useState(false);

  const localizar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = manual ? { ...loc } : { cep };
      const r = await axios.post(`${API}/particular/localizar`, body);
      if (r.data.cep_encontrado === false) {
        toast.error("CEP não encontrado. Informe estado, cidade e bairro manualmente.");
        setManual(true);
        return;
      }
      setResultado(r.data);
      setLoc({ estado: r.data.estado, cidade: r.data.cidade, bairro: r.data.bairro });
      setEtapa("resultado");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const pagar = async () => {
    if (!aceite) { toast.error("É necessário aceitar o termo para continuar"); return; }
    setLoading(true);
    try {
      const r1 = await axios.post(`${API}/particular/conexao`, {
        estado: loc.estado, cidade: loc.cidade, bairro: loc.bairro, cep, aceite_termo: true,
      });
      const r2 = await axios.post(`${API}/particular/conexao/${r1.data.conexao_id}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = r2.data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-body">
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-2xl mx-auto px-5 py-3.5 flex items-center justify-between">
          <Link to="/" data-testid="particular-logo-link"><LogoAtivaJa size="sm" /></Link>
          <Link to="/qrcode" className="text-sm font-semibold text-[#142766] hover:text-[#FF6700] transition-colors" data-testid="particular-portal-link">
            Já tenho um pedido de loja
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-10">
        <div className="aj-fade-up">
          <div className="aj-eyebrow">Cliente Particular</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0F172A] mt-2">
            Encontre um <span className="aj-accent-orange">montador</span> na sua região
          </h1>
          <p className="text-[#64748B] mt-3 text-sm sm:text-base">
            Por apenas <strong>R$ 10,00</strong> você recebe o contato de até <strong>7 montadores</strong> avaliados
            perto de você. A negociação do serviço é feita diretamente com o montador.
          </p>
        </div>

        {etapa === "local" && (
          <form onSubmit={localizar} className="mt-8 aj-card p-6 sm:p-7 aj-fade-up-1" data-testid="particular-form-local">
            {!manual ? (
              <>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#142766]">Seu CEP</Label>
                <div className="mt-2 flex flex-col sm:flex-row gap-3">
                  <Input value={cep} onChange={(e) => setCep(e.target.value)} placeholder="00000-000"
                    className="h-12 text-lg font-mono rounded-lg border-[#CBD5E1] focus-visible:ring-[#FF6700]"
                    data-testid="particular-cep-input" required />
                  <button type="submit" disabled={loading}
                    className="btn-aj-primary h-12 justify-center whitespace-nowrap disabled:opacity-60"
                    data-testid="particular-buscar-btn">
                    <Search size={18} /> {loading ? "Buscando…" : "Buscar montadores"}
                  </button>
                </div>
                <button type="button" onClick={() => setManual(true)}
                  className="mt-4 text-sm font-semibold text-[#142766] hover:text-[#FF6700] transition-colors"
                  data-testid="particular-nao-sei-cep">
                  Não sei o CEP — informar cidade manualmente
                </button>
              </>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-[#142766]">Estado (UF)</Label>
                    <Input value={loc.estado} onChange={(e) => setLoc(l => ({...l, estado: e.target.value.toUpperCase().slice(0,2)}))}
                      placeholder="SP" className="mt-2 rounded-lg" data-testid="particular-estado" required />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-[#142766]">Cidade</Label>
                    <Input value={loc.cidade} onChange={(e) => setLoc(l => ({...l, cidade: e.target.value}))}
                      placeholder="São Paulo" className="mt-2 rounded-lg" data-testid="particular-cidade" required />
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="text-xs font-bold uppercase tracking-wider text-[#142766]">Bairro</Label>
                  <Input value={loc.bairro} onChange={(e) => setLoc(l => ({...l, bairro: e.target.value}))}
                    placeholder="Centro" className="mt-2 rounded-lg" data-testid="particular-bairro" />
                </div>
                <div className="mt-4 flex gap-3">
                  <button type="submit" disabled={loading} className="btn-aj-primary flex-1 justify-center disabled:opacity-60" data-testid="particular-buscar-manual-btn">
                    <Search size={17} /> {loading ? "Buscando…" : "Buscar montadores"}
                  </button>
                  <button type="button" onClick={() => setManual(false)}
                    className="px-4 rounded-lg text-sm font-semibold text-[#64748B] hover:bg-[#F1F5F9] transition-colors">
                    Usar CEP
                  </button>
                </div>
              </>
            )}
          </form>
        )}

        {etapa === "resultado" && resultado && (
          <div className="mt-8 space-y-4 aj-fade-up" data-testid="particular-resultado">
            <div className="aj-card p-6">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#142766] mb-3">
                <MapPin size={14} className="text-[#FF6700]" /> {resultado.cidade}/{resultado.estado} {resultado.bairro && `· ${resultado.bairro}`}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] p-4 text-center">
                  <div className="font-display font-extrabold text-4xl text-[#142766]" data-testid="particular-total-cidade">{resultado.total_cidade}</div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-[#64748B] mt-1">na cidade</div>
                </div>
                <div className="rounded-lg bg-[#FFF3E0] border border-[#FDBA74] p-4 text-center">
                  <div className="font-display font-extrabold text-4xl text-[#E05C00]" data-testid="particular-total-bairro">{resultado.total_bairro}</div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-[#E05C00] mt-1">próximos ao bairro</div>
                </div>
              </div>
            </div>

            {resultado.montadores_preview?.length > 0 && (
              <div className="space-y-2" data-testid="particular-preview">
                <div className="aj-eyebrow">Montadores que você vai desbloquear</div>
                {resultado.montadores_preview.map((m, i) => (
                  <div key={i} className="aj-card p-4 flex items-center justify-between gap-3" data-testid={`particular-preview-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#142766] text-white flex items-center justify-center font-display font-bold">
                        {m.nome_parcial?.[0] || "M"}
                      </div>
                      <div>
                        <div className="font-semibold text-[#0F172A] text-sm">{m.nome_parcial}
                          {m.documentos_verificados && (
                            <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-bold text-[#166534] bg-[#DCFCE7] px-1.5 py-0.5 rounded"
                              data-testid={`preview-selo-${i}`}>✓ Documentos verificados</span>
                          )}
                        </div>
                        <div className="text-xs text-[#64748B]">{m.cidade}{m.bairro ? ` · ${m.bairro}` : ""}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      {m.nota_media != null && (
                        <span className="inline-flex items-center gap-1 font-bold text-[#B45309]">
                          <Star size={13} className="fill-[#F59E0B] text-[#F59E0B]" /> {m.nota_media}
                        </span>
                      )}
                      <span className="text-[#64748B]"><strong className="text-[#0F172A]">{m.servicos_concluidos}</strong> concluído(s)</span>
                      <span className="px-2 py-1 rounded-md bg-[#F1F5F9] text-[#94A3B8] font-mono text-[10px] tracking-wider">
                        (11) •••••-••••
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {resultado.total_cidade === 0 ? (
              <div className="aj-card p-6 border-l-4 border-l-[#EF4444]" data-testid="particular-sem-montadores">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#DC2626] mb-2">
                  <XCircle size={14} /> Sem montadores na sua região
                </div>
                <p className="text-sm text-[#334155]">
                  Ainda não temos montadores cadastrados em {resultado.cidade}/{resultado.estado}.
                  Nenhum valor será cobrado. Tente novamente em breve!
                </p>
                <button onClick={() => { setEtapa("local"); setResultado(null); }}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#142766] hover:text-[#FF6700]" data-testid="particular-tentar-outra">
                  <ArrowLeft size={15} /> Buscar outra região
                </button>
              </div>
            ) : (
              <>
                {resultado.total_cidade < 7 && (
                  <div className="aj-card p-5 border-l-4 border-l-[#F59E0B]" data-testid="particular-aviso-poucos">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#B45309] mb-1">
                      <AlertTriangle size={14} /> Atenção
                    </div>
                    <p className="text-sm text-[#334155]">
                      Sua região tem apenas <strong>{resultado.total_cidade} montador(es)</strong> (menos que os 7 do plano).
                      Você receberá o contato de todos os disponíveis. Deseja continuar mesmo assim?
                    </p>
                  </div>
                )}
                <div className="flex gap-3">
                  <button onClick={() => setEtapa("termo")} className="btn-aj-primary flex-1 h-12 justify-center" data-testid="particular-continuar">
                    Sim, continuar <ArrowRight size={16} />
                  </button>
                  <button onClick={() => { setEtapa("local"); setResultado(null); }}
                    className="px-5 rounded-lg border-[1.5px] border-[#CBD5E1] text-sm font-semibold text-[#64748B] hover:border-[#142766] hover:text-[#142766] transition-colors" data-testid="particular-desistir">
                    Não, voltar
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {etapa === "termo" && resultado && (
          <div className="mt-8 space-y-4 aj-fade-up" data-testid="particular-termo">
            <div className="aj-card p-6 border-l-4 border-l-[#142766]">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#142766] mb-3">
                <ShieldAlert size={14} /> Termo de ciência — leia antes de pagar
              </div>
              <pre className="whitespace-pre-wrap text-xs text-[#334155] font-body leading-relaxed max-h-64 overflow-y-auto bg-[#F8FAFC] rounded-lg p-4 border border-[#E2E8F0]" data-testid="particular-termo-texto">
{resultado.termo}
              </pre>
              <label className="flex items-start gap-3 mt-4 cursor-pointer" data-testid="particular-aceite-label">
                <input type="checkbox" checked={aceite} onChange={(e) => setAceite(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#FF6700]" data-testid="particular-aceite-check" />
                <span className="text-sm text-[#334155]">
                  Li e estou ciente de que a plataforma <strong>não se responsabiliza por garantia após a montagem</strong> e
                  <strong> não garante que o montador fechará o negócio</strong> — o serviço contratado é apenas a conexão.
                </span>
              </label>
            </div>
            <button onClick={pagar} disabled={loading || !aceite}
              className="btn-aj-primary w-full h-12 justify-center disabled:opacity-50" data-testid="particular-pagar">
              <CreditCard size={17} /> {loading ? "Redirecionando…" : "Aceitar e pagar R$ 10,00"}
            </button>
            <button onClick={() => setEtapa("resultado")} className="w-full text-sm font-semibold text-[#64748B] hover:text-[#142766] transition-colors" data-testid="particular-voltar-resultado">
              Voltar
            </button>
          </div>
        )}

        <div className="mt-10 flex items-center gap-2 text-xs text-[#94A3B8]">
          <Users size={14} /> Montadores verificados, com contrato digital assinado e avaliações reais de clientes.
        </div>
      </main>
    </div>
  );
}
